/**
 * Activation Checklist for Tenant Onboarding (DRR-073)
 *
 * Mirrors the 6 activation criteria from rules/core/tenant-activation-criteria config:
 * 1. CRITICAL workflows approved (dual approval)
 * 2. HIGH rules reviewed (single approval)
 * 3. SubgraphMetadata seeded
 * 4. Authentik tenant group created
 * 5. Admin user provisioned
 * 6. DynamoDB access verified
 *
 * All criteria must pass before tenant can be activated.
 * Fail-closed: any failing criterion blocks activation with remediation guidance.
 *
 * FERP Principles:
 * - Data-driven: Criteria status from backend
 * - Fail-closed: Blocks activation on any failure
 */

import {
  Stack,
  Card,
  Text,
  Group,
  Alert,
  ThemeIcon,
  List,
} from '@mantine/core';
import {
  IconShieldCheck,
  IconAlertTriangle,
  IconDatabase,
  IconUsers,
  IconKey,
  IconPuzzle,
  IconChecks,
} from '@tabler/icons-react';
import { GqlOnboardingStatus } from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

export interface ActivationChecklistProps {
  tenantId: string;
  onboardingStatus: string;
}

// ============================================================================
// Criteria definitions (mirrors tenant-activation-criteria config)
// ============================================================================

interface CriterionDef {
  id: string;
  label: string;
  description: string;
  icon: typeof IconShieldCheck;
  remediation: string;
}

const CRITERIA: CriterionDef[] = [
  {
    id: 'critical-workflows',
    label: 'CRITICAL Workflows Approved',
    description: 'All CRITICAL safety workflows must have dual approval',
    icon: IconShieldCheck,
    remediation: 'Review and approve all CRITICAL workflows in the Approval Queue tab',
  },
  {
    id: 'high-rules',
    label: 'HIGH Rules Reviewed',
    description: 'All HIGH safety rules must have single approval',
    icon: IconShieldCheck,
    remediation: 'Review and approve all HIGH rules in the Approval Queue tab',
  },
  {
    id: 'subgraph-metadata',
    label: 'SubgraphMetadata Seeded',
    description: 'Backend returns routes, navigation, and entity types',
    icon: IconPuzzle,
    remediation: 'Run: python3 ./scripts/seed-tenants-with-filtering.py --filter',
  },
  {
    id: 'authentik-group',
    label: 'Authentik Group Created',
    description: 'OIDC authentication configured for tenant',
    icon: IconKey,
    remediation: 'Run: ./scripts/seed/provision-tenant.sh step 4 (Authentik group)',
  },
  {
    id: 'admin-user',
    label: 'Admin User Provisioned',
    description: 'At least one admin user can log in',
    icon: IconUsers,
    remediation: 'Create an admin user in Authentik and assign to the tenant group',
  },
  {
    id: 'dynamodb-access',
    label: 'DynamoDB Access Verified',
    description: 'Read/write test passes for tenant partition',
    icon: IconDatabase,
    remediation: 'Run: ./scripts/seed/provision-tenant.sh step 1 (TenantRegistry) and verify',
  },
];

// ============================================================================
// Component
// ============================================================================

export function ActivationChecklist({
  tenantId,
  onboardingStatus,
}: ActivationChecklistProps): React.ReactElement {
  // Already active — no checklist needed
  if (onboardingStatus === GqlOnboardingStatus.ACTIVE) {
    return (
      <Alert color="green" title="Tenant Active" icon={<IconChecks size={16} />}>
        This tenant is fully onboarded and active. All activation criteria were met.
      </Alert>
    );
  }

  // Backend resolver for checkActivationCriteria does not exist yet.
  // Show static criteria reference with CLI verification path.
  return (
    <Stack gap="md">
      <Alert color="yellow" title="Activation Criteria" icon={<IconAlertTriangle size={16} />}>
        Activation criteria check is not yet available from the backend.
        Use the CLI to verify: <code>python3 ./scripts/onboard-tenant.py --tenant {tenantId} --check-activation</code>
      </Alert>

      {/* Static criteria reference */}
      <Card withBorder>
        <Text fw={600} mb="md">Required Criteria (6 checks)</Text>
        <List spacing="sm">
          {CRITERIA.map((criterion) => {
            const CriterionIcon = criterion.icon;
            return (
              <List.Item
                key={criterion.id}
                icon={
                  <ThemeIcon size="sm" variant="light" color="gray">
                    <CriterionIcon size={12} />
                  </ThemeIcon>
                }
              >
                <Group gap="xs">
                  <Text size="sm" fw={500}>{criterion.label}</Text>
                </Group>
                <Text size="xs" c="dimmed">{criterion.description}</Text>
                <Text size="xs" c="dimmed">Fix: {criterion.remediation}</Text>
              </List.Item>
            );
          })}
        </List>
      </Card>

      {/* CLI reference */}
      <Card withBorder>
        <Text fw={600} mb="xs">CLI Equivalent</Text>
        <Text size="sm" c="dimmed">
          <code>python3 ./scripts/onboard-tenant.py --tenant {tenantId} --check-activation</code>
        </Text>
      </Card>
    </Stack>
  );
}

export default ActivationChecklist;
